import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import { colors } from '../../theme/colorTokens';

const clusterKeys = [
  {
    titleKey: 'home.contentClusters.cluster1Title',
    guides: [
      { labelKey: 'home.contentClusters.cluster1Guide1', route: 'blogCredito' },
      { labelKey: 'home.contentClusters.cluster1Guide2', route: 'blogTarjetas' },
      { labelKey: 'home.contentClusters.cluster1Guide3', route: 'creditCards' },
    ],
    footerLabelKey: 'home.contentClusters.cluster1Footer',
    footerRoute: 'financlopedia',
  },
  {
    titleKey: 'home.contentClusters.cluster2Title',
    guides: [
      { labelKey: 'home.contentClusters.cluster2Guide1', route: 'blogGuiaQuebec' },
      { labelKey: 'home.contentClusters.cluster2Guide2', route: 'blogT4' },
      { labelKey: 'home.contentClusters.cluster2Guide3', route: 'blogCRAvsRQ' },
    ],
    footerLabelKey: 'home.contentClusters.cluster2Footer',
    footerRoute: 'taxCalendar',
  },
  {
    titleKey: 'home.contentClusters.cluster3Title',
    guides: [
      { labelKey: 'home.contentClusters.cluster3Guide1', route: 'blogHipoteca' },
      { labelKey: 'home.contentClusters.cluster3Guide2', route: 'mortgageCalc' },
      { labelKey: 'home.contentClusters.cluster3Guide3', route: 'mortgageComparator' },
    ],
    footerLabelKey: 'home.contentClusters.cluster3Footer',
    footerRoute: 'mortgages',
  },
  {
    titleKey: 'home.contentClusters.cluster4Title',
    guides: [
      { labelKey: 'home.contentClusters.cluster4Guide1', route: 'blogTFSA' },
      { labelKey: 'home.contentClusters.cluster4Guide2', route: 'blogRRSP' },
      { labelKey: 'home.contentClusters.cluster4Guide3', route: 'budgetPlanner' },
    ],
    footerLabelKey: 'home.contentClusters.cluster4Footer',
    footerRoute: 'financlopedia',
  },
];

const ContentClusters = () => {
  const lp = useLocalizedPath();
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        backgroundColor: colors.bgSoft,
        pt: { xs: 6, md: 8 },
        pb: { xs: 8, md: 10 },
      }}
    >
      <Container maxWidth="lg">
        {/* Overline */}
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: colors.textSecondary,
            textTransform: 'uppercase',
            letterSpacing: '2px',
            mb: 1.5,
          }}
        >
          {t('home.contentClusters.overline')}
        </Typography>

        {/* Headline */}
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: colors.textPrimary,
            fontSize: { xs: '26px', md: '34px' },
            lineHeight: 1.2,
            mb: 1.5,
          }}
        >
          {t('home.contentClusters.title')}
        </Typography>

        {/* Subtitle */}
        <Typography
          sx={{
            fontSize: '16px',
            color: colors.textSecondary,
            lineHeight: 1.6,
            maxWidth: '60ch',
            mb: 4,
          }}
        >
          {t('home.contentClusters.subtitle')}
        </Typography>

        {/* Cluster cards */}
        <Grid container spacing={4}>
          {clusterKeys.map((cluster) => (
            <Grid item xs={12} md={6} key={cluster.titleKey}>
              <Box
                sx={{
                  backgroundColor: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '14px',
                  p: '24px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 1px 6px rgba(0,0,0,0.03)',
                  transition:
                    'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 8px 28px rgba(0,0,0,0.08)',
                  },
                }}
              >
                {/* Cluster title with left accent */}
                <Typography
                  sx={{
                    fontSize: '20px',
                    fontWeight: 700,
                    color: colors.textPrimary,
                    borderLeft: `3px solid ${colors.primary}`,
                    pl: '12px',
                    mb: 3,
                    lineHeight: 1.25,
                  }}
                >
                  {t(cluster.titleKey)}
                </Typography>

                {/* Guide links */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    flex: 1,
                  }}
                >
                  {cluster.guides.map((guide) => (
                    <Box
                      key={guide.labelKey}
                      component={RouterLink}
                      to={lp(guide.route)}
                      sx={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 400,
                        color: colors.primary,
                        textDecoration: 'none',
                        lineHeight: 1.5,
                        py: { xs: '10px', md: '4px' },
                        minHeight: { xs: 44, md: 'auto' },
                        transition: 'color 0.15s ease',
                        '&:hover': {
                          color: colors.primaryHover,
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {t(guide.labelKey)}
                    </Box>
                  ))}
                </Box>

                {/* Footer link pushed to bottom */}
                <Box
                  component={RouterLink}
                  to={lp(cluster.footerRoute)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: colors.textSecondary,
                    textDecoration: 'none',
                    mt: 3,
                    pt: 2,
                    minHeight: { xs: 44, md: 'auto' },
                    borderTop: `1px solid ${colors.border}`,
                    transition: 'color 0.15s ease',
                    '&:hover': {
                      color: colors.primary,
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {`${t(cluster.footerLabelKey)} \u2192`}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default ContentClusters;
